import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  Avatar,
  Chip,
  CircularProgress,
  Paper
} from "@mui/material";
import { MdClose, MdSend } from "react-icons/md";
import { TASKCHAT, TASKCHATALL } from "../../services/actions";
import VoiceMicButton from "../../components/VoiceMicButton";

export default function TaskChatModal({ open, onClose, task }) {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state?.loginState?.auth);
  const taskchatallState = useSelector((state) => state?.taskchatallState);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  const currentTaskId = task?.work_task_id || task?.id;

  const fetchChats = () => {
    if (currentTaskId) {
      const url = `task-chat/?task_id=${currentTaskId}`;
      dispatch({ type: `${TASKCHATALL}_GET`, payload: url });
    }
  };

  useEffect(() => {
    if (open && currentTaskId) {
      fetchChats();
      const interval = setInterval(fetchChats, 4000);
      return () => clearInterval(interval);
    }
  }, [open, currentTaskId]);

  useEffect(() => {
    if (open) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [taskchatallState?.data?.results, open]);

  const handleSend = () => {
    if (!message.trim() || !currentTaskId) return;
    setSending(true);

    dispatch({
      type: `${TASKCHAT}_POST`,
      payload: {
        task: currentTaskId,
        message: message.trim()
      },
      callback: () => {
        setMessage("");
        setSending(false);
        fetchChats();
      }
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const chats = (taskchatallState?.data?.results || []).filter(
    (c) => Number(c.task) === Number(currentTaskId)
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: "16px",
          height: "620px",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)"
        }
      }}
    >
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #e2e8f0", pb: 1.5, pt: 1.5, backgroundColor: "#ffffff" }}>
        <Box>
          <Typography variant="subtitle1" fontWeight={700} color="#0f172a">
            💬 Task Discussion: {task?.task_name || "Task"}
          </Typography>
          <Typography variant="caption" color="#64748b">
            Work: <strong>{task?.work_name || "Work"}</strong> {task?.assigned_to_name ? `| Assignee: ${task.assigned_to_name}` : ""}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: "#64748b", "&:hover": { bgcolor: "#f1f5f9" } }}>
          <MdClose size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ flexGrow: 1, p: 2.5, backgroundColor: "#f8fafc", overflowY: "auto", display: "flex", flexDirection: "column", gap: 1.5 }}>
        {taskchatallState?.loading && chats.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
            <CircularProgress size={28} />
          </Box>
        ) : chats.length === 0 ? (
          <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", height: "100%", color: "#94a3b8", gap: 1 }}>
            <Typography variant="body2" fontWeight={600} color="#64748b">No messages yet</Typography>
            <Typography variant="caption">Start the conversation between the assigned employee and manager.</Typography>
          </Box>
        ) : (
          chats.map((chat) => {
            const isMe = chat.sender === currentUser?.id || chat.sender_name === currentUser?.username;
            return (
              <Box key={chat.id} sx={{ display: "flex", flexDirection: "column", alignItems: isMe ? "flex-end" : "flex-start", maxWidth: "75%", alignSelf: isMe ? "flex-end" : "flex-start" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mb: 0.4 }}>
                  <Typography variant="caption" fontWeight={600} color="#475569">
                    {isMe ? "You" : chat.sender_name}
                  </Typography>
                  {chat.sender_role && (
                    <Chip label={chat.sender_role} size="small" sx={{ height: "18px", fontSize: "10px", fontWeight: 600, backgroundColor: isMe ? "#e0e7ff" : "#f1f5f9", color: isMe ? "#3730a3" : "#475569" }} />
                  )}
                  <Typography variant="caption" color="#94a3b8" fontSize="10px">
                    {chat.created_at_formatted}
                  </Typography>
                </Box>

                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    borderRadius: isMe ? "14px 14px 2px 14px" : "14px 14px 14px 2px",
                    backgroundColor: isMe ? "#2563eb" : "#ffffff",
                    color: isMe ? "#ffffff" : "#1e293b",
                    border: isMe ? "none" : "1px solid #e2e8f0",
                    boxShadow: isMe ? "0 4px 6px -1px rgba(37, 99, 235, 0.2)" : "0 1px 3px rgba(0,0,0,0.05)",
                    wordBreak: "break-word"
                  }}
                >
                  <Typography variant="body2" sx={{ fontSize: "13px", lineHeight: 1.4, whiteSpace: "pre-wrap" }}>
                    {chat.message}
                  </Typography>
                </Paper>
              </Box>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </DialogContent>

      <DialogActions sx={{ p: 1.5, px: 2, borderTop: "1px solid #e2e8f0", backgroundColor: "#ffffff" }}>
        <Box sx={{ display: "flex", width: "100%", gap: 1.2, alignItems: "center" }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Type a message or update (Press Enter to send)..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            multiline
            maxRows={3}
            inputProps={{
              style: {
                color: "#0f172a",
                fontSize: "13px",
                lineHeight: "1.5"
              }
            }}
            InputProps={{
              endAdornment: (
                <VoiceMicButton
                  initialText={message}
                  onTranscript={(text) => setMessage(text)}
                />
              )
            }}
            sx={{
              backgroundColor: "#ffffff",
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                backgroundColor: "#ffffff",
                color: "#0f172a",
                "& fieldset": {
                  borderColor: "#cbd5e1"
                },
                "&:hover fieldset": {
                  borderColor: "#94a3b8"
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#2563eb"
                }
              },
              "& .MuiInputBase-input": {
                color: "#0f172a !important",
                WebkitTextFillColor: "#0f172a !important"
              },
              "& .MuiInputBase-input::placeholder": {
                color: "#94a3b8 !important",
                opacity: 1
              }
            }}
          />
          <Button
            variant="contained"
            onClick={handleSend}
            disabled={!message.trim() || sending}
            sx={{
              minWidth: "46px",
              width: "46px",
              height: "40px",
              borderRadius: "10px",
              backgroundColor: "#2563eb",
              "&:hover": { backgroundColor: "#1d4ed8" },
              p: 0
            }}
          >
            {sending ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : <MdSend size={18} />}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
